import * as React from "react";
import { useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { vorgangService, type TodoDto } from "../../../services/vorgangService";
import { VorgangPickerAuswahl } from "../picker/VorgangPickerAuswahl";
import { IcoX } from "../v3/vg3Icons";
import "../v3/vorgangV3.css";
import "../../calendar/calendar.css";

/**
 * EIN TO-DO IN EINEN ANDEREN VORGANG BRINGEN (PO 22.09.2026).
 *
 * > „bei einem To-do muss ich dann aber auch bei ⋯ ein To-do zum Vorgang
 * >  verschieben können"
 *
 * ══ DER GEGENWEG ZU „TO-DO HIERHER VERSCHIEBEN" ══════════════════════════
 * Bis hierher wanderte ein To-do nur „von hinten": Man ging in den ZIEL-Vorgang
 * und holte es. Das setzt voraus, dass man den Zielvorgang schon offen hat —
 * wer aber gerade auf das To-do schaut und merkt, dass es woanders hingehört,
 * musste erst wegnavigieren und dann suchen.
 *
 * ══ DAS ZIEL IST EIN VORGANG, KEIN TO-DO ═════════════════════════════════
 * Anders als beim Zuordnen von Mails oder Anrufen: Dort sucht man den Ort, an
 * dem etwas HÄNGT, und der ist bei einem gegliederten Vorgang eines seiner
 * To-dos. Hier ist das To-do selbst die Fracht — es wird drüben ein To-do
 * neben den anderen. Deshalb die schlichte Vorgangsliste und nicht
 * `VorgangZielAuswahl`.
 *
 * Trug der Zielvorgang bis eben seinen Schriftwechsel selbst, zieht der Server
 * ihn beim Ankommen in ein erstes To-do (`uebernimmVorgangsstrang`) — sonst
 * stünde er mit To-do UND freiem Eigenen da.
 */
export const TodoHinausDialog: React.FC<{
    todo: TodoDto;
    contactId: number | null;
    /** Der Vorgang, in dem das To-do heute liegt — er ist kein Ziel. */
    aktuellerVorgangId: number;
    onVerschoben: () => void | Promise<void>;
    onSchliessen: () => void;
}> = ({ todo, contactId, aktuellerVorgangId, onVerschoben, onSchliessen }) => {
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const waehlen = async (vorgangId: number) => {
        if (busy) return;
        // Der eigene Vorgang ist kein Ziel — der Server schriebe nur dieselbe
        // Zeile neu, und der Anwender hielte es für eine Verschiebung.
        if (vorgangId === aktuellerVorgangId) {
            setFehler("Das To-do liegt bereits in diesem Vorgang.");
            return;
        }
        setBusy(true);
        setFehler(null);
        try {
            await vorgangService.verschiebeTodo(todo.id, vorgangId);
            await onVerschoben();
            onSchliessen();
        } catch (err: any) {
            console.error("[TodoHinausDialog] Verschieben fehlgeschlagen", err);
            setFehler(err?.message ?? "Verschieben fehlgeschlagen.");
            setBusy(false);
        }
    };

    return createPortal(
        <div
            className="vg4-schleier"
            onMouseDown={(e) => e.target === e.currentTarget && !busy && onSchliessen()}
        >
            <div className="kvp-rd vg3-modal" role="dialog" aria-modal="true" aria-label="To-do verschieben">
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">To-do verschieben</span>
                    <button
                        type="button"
                        className="vg3-ibtn"
                        title="Schließen"
                        aria-label="Schließen"
                        disabled={busy}
                        onClick={onSchliessen}
                    >
                        <IcoX size={16} />
                    </button>
                </div>

                {fehler && (
                    <div className="vg3-fehler" role="alert">
                        {fehler}
                    </div>
                )}

                <div className="vg3-modal-body">
                    <p className="vg3-modal-hinweis">
                        Wohin soll „{todo.titel || `To-do ${todo.id}`}“? Es wechselt dorthin — mit
                        allem, was daran hängt: Notizen, Mails, Anrufe, Termine und Dokumente. In
                        diesem Vorgang steht es danach nicht mehr.
                    </p>
                    <VorgangPickerAuswahl
                        onPick={(v) => void waehlen(v.id)}
                        contactId={contactId}
                        aktuelleVorgangId={aktuellerVorgangId}
                        disabled={busy}
                    />
                </div>

                <div className="vg3-modal-fuss">
                    <button type="button" className="vg3-btn" disabled={busy} onClick={onSchliessen}>
                        Abbrechen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default TodoHinausDialog;
